import { useContext, useEffect, useRef } from 'react'
import firebase from '../../firebase/compat'
import { db, bucket } from '../../firebase/config'
import { SavedIcon } from '../../StateManagment/SavedIcon'
import buildUploadPath from '../../helpers/uploads/buildUploadPath';

/**
 * Gedeeld huisstijl-blok (logo + drie merkkleuren op CompagnyMeta).
 * Eén schrijfpad, twee plekken: Instellingen → Algemeen én de Huisstijl-tab
 * van Impact in beeld. Handlers blijven per veld (zie
 * docs/pitfalls.md#state-handler-overwrites-sibling-field).
 *
 * @param {object} props
 * @param {object} props.comp - CompagnyMeta row (with docid, Logo, PrimaryColor, SecundairyColor, TertairyColor)
 * @param {boolean} props.canEdit - Whether the current user may edit settings
 */
const HuisstijlSettings = ({ comp, canEdit }) => {
    const [saved, setSaved] = useContext(SavedIcon)

    // De native color-picker vuurt onChange bij elke sleepbeweging — zonder
    // debounce betekent één kleurkeuze tientallen Firestore-writes. Per veld
    // één timer; bij unmount wordt een openstaande write direct uitgevoerd
    // (flush), zodat de laatste keuze nooit verloren gaat.
    /** @type {React.MutableRefObject<Object<string, {timer: number, write: () => void}>>} */
    const pendingWrites = useRef({})

    useEffect(() => () => {
        Object.values(pendingWrites.current).forEach(({ timer, write }) => {
            clearTimeout(timer)
            write()
        })
        pendingWrites.current = {}
    }, [])

    // Schrijft alléén het eigen veld — nooit een sibling meenemen.
    // AI-CONTEXT: See docs/pitfalls.md#state-handler-overwrites-sibling-field
    const queueColorWrite = (field, value) => {
        const existing = pendingWrites.current[field]
        if (existing) clearTimeout(existing.timer)

        const write = () => {
            delete pendingWrites.current[field]
            db.collection('CompagnyMeta')
            .doc(comp.docid)
            .update({ [field]: value })
            .then(() => setSaved('flex'))
        }

        pendingWrites.current[field] = { timer: setTimeout(write, 400), write }
    }

    const LogoHandler = (e) => {

        const logo = e.target.files[0]

        // Uniek pad: een ruwe bestandsnaam laat twee mensen met dezelfde
        // 'foto.jpg' elkaars afbeelding overschrijven.
        // AI-CONTEXT: See docs/inbox.md #122
        const storageRef = bucket.ref(buildUploadPath(logo.name, "ProfilePhotos"));
        const uploadTask = storageRef.put(logo)

        uploadTask.then(() => {

            uploadTask.on('state_changed', snapshot => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            switch (snapshot.state) {
            case firebase.storage.TaskState.PAUSED:
                console.log('Upload is paused');
                break;
            case firebase.storage.TaskState.RUNNING:
                console.log('Upload is running');
                break;
            }
            }, (err) => {
                alert(err)
            }, () => {
            uploadTask.snapshot.ref.getDownloadURL().then(downloadURL => {

            saveLogo(downloadURL)

                })
            })
        }).catch((err) => {
            // Zonder deze afhandeling verdwijnt een geweigerde upload (te groot,
            // of tegengehouden door storage.rules) in een niet-afgevangen
            // belofte: geen melding, en de gebruiker ziet niets gebeuren.
            alert(`Uploaden mislukt: ${err?.message || err}`)
        })
    }

    const saveLogo = (banner) => {
        db.collection("CompagnyMeta")
        .doc(comp.docid)
        .update({
            Logo: banner
        })
        .then(() => {
            setSaved('flex')
        })
    }

    const primairyColorHandler = (e) => queueColorWrite('PrimaryColor', e.target.value)

    const secundairyColorHandler = (e) => queueColorWrite('SecundairyColor', e.target.value)

    const tertairyColorHandler = (e) => queueColorWrite('TertairyColor', e.target.value)

    return (
        <>
            <div className="dashboard-container logo-container">
                <h2>Logo</h2>
                <img src={comp.Logo} alt="Logo" />
                <input className="input-classic" type="file" onChange={LogoHandler} disabled={!canEdit} />
            </div>
            <div className="dashboard-container logo-container">
                <h2>Huisstijl</h2>
                <div className="settings-color-container">
                    <div className="settings-color-picker-container">
                        <p>Primair</p>
                        <div className="settings-color-box" style={{backgroundColor: comp.PrimaryColor}}></div>
                        <input className="setting-color-picker" type="color" aria-label="Primair" defaultValue={comp.PrimaryColor} onChange={primairyColorHandler} disabled={!canEdit} />
                    </div>
                    <div className="settings-color-picker-container">
                        <p>Secundair</p>
                        <div className="settings-color-box" style={{backgroundColor: comp.SecundairyColor}}></div>
                        <input className="setting-color-picker" type="color" aria-label="Secundair" defaultValue={comp.SecundairyColor} onChange={secundairyColorHandler} disabled={!canEdit} />
                    </div>
                    <div className="settings-color-picker-container">
                        <p>Tertiair</p>
                        <div className="settings-color-box" style={{backgroundColor: comp.TertairyColor}}></div>
                        <input className="setting-color-picker" type="color" aria-label="Tertiair" defaultValue={comp.TertairyColor} onChange={tertairyColorHandler} disabled={!canEdit} />
                    </div>
                </div>
            </div>
        </>
    )
}

export default HuisstijlSettings
